// Auth Types
export interface SignUpRequest {
  username: string;
  email: string;
  password: string;
  first_name: string;
  last_name: string;
}

export interface SignInRequest {
  username: string;
  password: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ForgotPasswordResponse {
  detail: string;
}

export interface VerifyOtpRequest {
  email: string;
  otp: string;
}

export interface VerifyOtpResponse {
  reset_token: string;
  detail?: string;
  message?: string;
}

export interface SetPasswordRequest {
  reset_token: string;
  password: string;
}

export interface SetPasswordResponse {
  detail?: string;
  message?: string;
}

export interface ChangePasswordRequest {
  current_password: string;
  password: string;
}

/** Cookie-auth login / refresh / logout response — no JWTs in the body. */
export interface AuthResponse {
  detail: string;
}

// Error Types
export interface ApiError {
  message?: string;
  status?: number;
  fieldErrors?: Record<string, string>;
  response?: {
    status?: number;
    data?: Record<string, unknown> | {
      detail?: string;
    };
  };
}

/** Collect Django-style field validation messages without paraphrasing. */
const extractFieldMessages = (data: Record<string, unknown>): string[] => {
  const messages: string[] = [];
  for (const [key, val] of Object.entries(data)) {
    if (key === 'detail' || key === 'message') continue;
    if (Array.isArray(val)) {
      messages.push(...val.filter((v): v is string => typeof v === 'string'));
    } else if (typeof val === 'string') {
      messages.push(val);
    }
  }
  return messages;
};

// Common error handling utility
export const handleApiError = (err: unknown): string => {
  const apiError = err as ApiError;
  const status = apiError.status ?? apiError.response?.status;

  if (status === 429) {
    return 'Too many attempts. Please wait before trying again.';
  }

  const data = apiError.response?.data;
  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>;
    if (typeof record.detail === 'string') {
      return record.detail;
    }
    const fieldMessages = extractFieldMessages(record);
    if (fieldMessages.length > 0) {
      return fieldMessages.join(' ');
    }
  }

  // ParsedApiError from BaseApi.parseError (Axios already transformed)
  if (apiError.fieldErrors && Object.keys(apiError.fieldErrors).length > 0) {
    const fieldMessages = Object.entries(apiError.fieldErrors)
      .filter(([key]) => key !== 'detail' && key !== 'message')
      .map(([, msg]) => msg)
      .filter(Boolean);
    if (fieldMessages.length > 0) {
      return fieldMessages.join(' ');
    }
    if (apiError.fieldErrors.detail) {
      return apiError.fieldErrors.detail;
    }
  }

  return apiError.message || 'An unexpected error occurred. Please try again.';
};

// School Types
export interface School {
  id: string;
  name: string;
  address: string;
  contactNumber: string;
  email: string;
}

export interface CreateSchoolRequest {
  name: string;
  address: string;
  contactNumber: string;
  email: string;
}

export interface UpdateSchoolRequest extends Partial<CreateSchoolRequest> {
  id: string;
}

// Common Types
export interface ApiResponse<T> {
  data: T;
  message: string;
  status: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

export interface Exam {
  id: number;
  name: string;
  description: string;
  total_items: number;
  time_allotment_minutes: number;
  grade_level?: string;
  batch?: string;
  is_practice_exam: boolean;
  subject: string;
  school: number;
  datetime_created: string;
  datetime_updated: string;
  questions?: Question[];
  image_url?: string; // optional field for exam image
}

export interface ExamType {
  id: string;
  name: string;
  description?: string;
  total_items: number;
  time_allotment_minutes: number;
  school?: string;
  grade_level?: string;
  batch?: string;
  datetime_created: string;
  datetime_updated: string;
  is_practice_exam: boolean;
}

export interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  section: string;
  batch: string;
  role?: string;
  subject?: string;
  has_accepted_terms_and_conditions?: boolean;
  datetime_last_accepted_terms_and_conditions?: string;
  features?: string[];
}

export interface ExamSession {
  id: number;
  user: User;
  exam: Exam;
  is_practice_exam: boolean;
  datetime_session_start: string;
  datetime_session_end?: string;
  datetime_created: string;
  datetime_updated: string;
  total_time_spent_seconds?: number; // optional field for total time spent
  correct_answers?: number; // new field from backend
  total_score?: number; // total score from backend
  score_percentage?: number; // new field from backend
  analytics?: Analytics; // OpenAI analytics data
  answers?: Answer[]; // answers associated with this session
  status?: string; // session status (ongoing, completed, etc.)
  remaining_time_seconds?: number; // remaining time from backend
  attempt_number?: number; // attempt number from backend
  total_items?: number; // total questions from backend
  last_activity?: string; // last activity timestamp
  is_active?: boolean; // whether session is active
  is_first_attempt?: boolean; // whether this is first attempt
}

export interface Question {
  id: string;             // string ID for frontend consistency
  question: string;       // the question text (Django field: question)
  subject: string;
  subsubject?: string | null;
  explanation?: string | null;
  correct_answer?: "A" | "B" | "C" | "D" | null;  // Keep for backward compatibility
  correctAnswer?: "A" | "B" | "C" | "D" | null;   // Add camelCase version from backend
  choice_a?: string | null;
  choice_b?: string | null;
  choice_c?: string | null;
  choice_d?: string | null;
  choice_a_image?: string | null;  // Add choice image fields
  choice_b_image?: string | null;
  choice_c_image?: string | null;
  choice_d_image?: string | null;
  image?: string;         // if you support images for questions (optional)
  question_image?: string; // Add question_image field from backend
  is_active?: boolean;    // Add is_active field from backend
  datetime_created?: string; // Add datetime fields from backend
  datetime_updated?: string;
  max_points?: number;    // Maximum points for this question
  // You can add other fields you want from your Django model here
}


export interface Answer {
  id: number;
  question: number;
  exam_session: number;
  answer: 'A' | 'B' | 'C' | 'D';
  datetime_created: string;
  datetime_updated: string;
  score_given?: number | null;
  teacher_comment?: string | null;
}

export interface CreateExamSessionRequest {
  exam: number;
  datetime_session_start: string; // ISO date string
  continue_existing?: boolean; // optional, defaults to false
}

export interface UpdateExamSessionRequest {
  datetime_session_end: string;
}

export interface CreateAnswerRequest {
  question: number;
  answer: ValidAnswer;
  exam_session: number;
}

export interface QuestionDisplayProps {
  question: Question;
  selected: Answer | undefined;
  onSelect: (qid: string, opt: Answer) => void;
}

export interface AnswerMap {
  [questionId: string]: ValidAnswer | null
}

export type ValidAnswer = "A" | "B" | "C" | "D"

export interface AnswerItem {
  questionId: string;
  answer: string;
  firstSaved: boolean;
  answerId?: number; // from backend, initially undefined
  saving?: boolean;  // optional flag for inflight status
  error?: string;    // optional error info
}

export type ExamResult = {
  id: string;
  name: string;
  totalItems: number;
  score: number;
  dateTaken: string;
  isPractice: boolean;
};

export interface SubjectPerformanceEntry {
  total_questions: number;
  correct_answers: number;
  score_percentage: number;
}

export type SubjectPerformance = Record<string, SubjectPerformanceEntry>;

// OpenAI Insights Types
export interface OpenAIInsights {
  overview: string;
  subject_breakdown: {
    [subject: string]: {
      performance: string;
      strengths: string[];
      weaknesses: string[];
      observation: string;
    };
  };
  recommendations: {
    focus_subject: string;
    comprehension_improvement: string;
    knowledge_enhancement: string;
    strategic_study: string;
    leverage_strengths: string;
  };
}

export interface Analytics {
  id: number;
  user: number;
  analysis: string | null;
  session_type: string | null;
  scope: string | null;
  datetime_created: string;
  datetime_updated: string;
}

export interface OpenAIReportResponse {
  detail: string;
  analytics_id?: number;
  analytics?: string;
}

// Reviewer Types
// API Response type (from backend)
export interface ReviewerApiResponse {
  id: number;
  name: string;
  subject: string;
  subsubject?: string | string[];
  // Prefer explicit tags from API if available
  tags?: string[] | string;
  topic?: string | string[]; // may be a JSON string of array from BE
  topics?: string[]; // alternative BE shape
  description: string;
  grade_level: string;
  thumbnail_url: string;
  resource_url: string;
  is_active: boolean;
  datetime_created: string;
  datetime_updated: string;
}

// Frontend Reviewer type (used in components)
export interface Reviewer {
  id: string;
  name: string;
  subject: string;
  grade: string;
  thumbnail: string;
  pdfUrl: string;
  tags: string[];
  description?: string;
  dateAdded?: string;
}

export interface GetReviewersParams {
  grade_level?: string;
  subject?: string;
  subsubject?: string;
  topic?: string;
  search?: string;
  refreshKey?: number; // client-only, not sent to API
}

export interface CreateReviewerRequest {
  name: string;
  subject: string;
  subsubject?: string;
  // New preferred tags field
  tags?: string[];
  // Keep single topic for backward compatibility
  topic?: string;
  // Allow multiple topics (tags) to be sent; each will be appended as `topic` in form-data
  topics?: string[];
  description?: string;
  grade_level: string;
  resource: File; // PDF
  thumbnail?: File; // optional image
  is_active?: boolean;
}

export interface UpdateReviewerRequest {
  name?: string;
  subject?: string;
  subsubject?: string;
  // New preferred tags field
  tags?: string[];
  topic?: string;
  topics?: string[];
  description?: string;
  grade_level?: string;
  resource?: File;
  thumbnail?: File;
  is_active?: boolean;
}

export interface ReviewerProgressResponse {
  scroll_percentage: number;
  is_completed: boolean;
}

export interface UpdateReviewerProgressRequest {
  scroll_percentage: number;
}

// Grading types
export interface GradingSession {
  id: number;
  exam_session: ExamSession;
  student: User;
  exam: Exam;
  total_questions: number;
  graded_questions: number;
  pending_questions: number;
  datetime_created: string;
  datetime_updated: string;
}

export interface GradingAnswer {
  id: number;
  answer: Answer;
  question: Question;
  student_answer: 'A' | 'B' | 'C' | 'D' | string;
  uploaded_solution?: string; // URL to uploaded solution file/image
  partial_score?: number;
  max_points: number;
  feedback?: string;
  is_graded: boolean;
  graded_by?: User;
  datetime_graded?: string;
}

export interface SubmitGradeRequest {
  partial_score: number;
  feedback?: string;
}

export interface GradingSessionDetails {
  session: ExamSession;
  answers: GradingAnswer[];
  exam: Exam;
  student: User;
}